import { useState } from 'preact/hooks';
import { ChevronDown, ChevronUp } from 'lucide-preact';
import clsx from 'clsx';
import type { ComponentChildren } from 'preact';

/**
 * Collapsible bottom sheet. The drawer is click-only — no drag — so there's
 * no handle pill. The expand/collapse affordance is a single circular
 * toggle floating above the sheet edge: dark on light, soft drop shadow,
 * big enough to be an obvious tap target.
 *
 * The collapsed peek is the first row of the expanded content (passed in as
 * `collapsed`), so opening the drawer never re-flows or duplicates the same
 * numbers — it just reveals more rows below the same anchor row.
 */
export function BottomSheet({
  collapsed,
  expanded,
  initiallyOpen = false,
}: {
  collapsed: ComponentChildren;
  expanded: ComponentChildren;
  initiallyOpen?: boolean;
}) {
  const [open, setOpen] = useState(initiallyOpen);
  return (
    <section
      class={clsx(
        'pointer-events-auto absolute inset-x-0 bottom-0 z-10 rounded-t-2xl border-t border-border bg-surface/95 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.25)] backdrop-blur transition-transform',
      )}
      aria-label="Journey details"
    >
      {/* Floating toggle, anchored to the top edge of the sheet and lifted
          half-way above it so it reads as the obvious tap target. White
          fill + soft shadow + circular outline = real button, not a tab. */}
      <button
        type="button"
        class="absolute right-3 -top-5 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-surface text-text shadow-[0_4px_12px_-2px_rgba(0,0,0,0.18)] transition-transform hover:-translate-y-px active:translate-y-px"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={open ? 'Hide details' : 'Show details'}
      >
        {open ? <ChevronDown size={20} aria-hidden="true" /> : <ChevronUp size={20} aria-hidden="true" />}
      </button>
      {/* Collapsed peek: the first row of the expanded body. Same anchor
          row stays put when the sheet expands; everything else slides in
          beneath it. */}
      <div class="px-4 pt-4 pb-3">{collapsed}</div>
      <div
        class={clsx(
          'overflow-hidden transition-[max-height,opacity] duration-300 ease-out',
          open ? 'max-h-[70dvh] opacity-100 overflow-y-auto' : 'max-h-0 opacity-0',
        )}
        aria-hidden={!open}
      >
        <div class="px-4 pb-5">{expanded}</div>
      </div>
    </section>
  );
}
